/**
 * Account-area GA4 instrumentation.
 *
 * Publishes only `ocs:*` custom events through window.OCSAnalyticsEvents
 * (assets/ocs-analytics-events.js), the sole owner of the Shopify analytics
 * publish call — see the ARCH-001 guard in tests/analytics. The Custom Pixel
 * owns the dataLayer mapping: nothing here touches dataLayer, and nothing here
 * may, because GTM runs inside the Web Pixel sandbox and the storefront has no
 * dataLayer at all.
 *
 * This replaces two production mechanisms that cannot survive that move:
 *   1. js_theme.js pushing myaccount_* events to a page-level dataLayer.
 *   2. GTM Click triggers and Custom HTML tags reading the storefront DOM
 *      (myaccount_loginlocation, and the CTA / section pushes). Sandboxed GTM
 *      cannot see the DOM, so those hooks are published from here instead.
 *
 * Event names and payload values are deliberately identical to production, so
 * the existing GTM container tags, triggers and dataLayer variables keep
 * working unchanged.
 *
 * Loaded globally (snippets/scripts.liquid) rather than per-section, because
 * the header account click happens on every page. Every handler gates on its
 * own marker, so non-account pages pay for a handful of querySelector calls.
 */
(function () {
  'use strict';

  var EVENT_LOGIN = 'ocs:myaccount_login';
  var EVENT_CREATION = 'ocs:myaccount_creation';
  var EVENT_PASSWORD = 'ocs:myaccount_password';
  var EVENT_ADDRESSES = 'ocs:myaccount_addresses';
  var EVENT_LOGIN_LOCATION = 'ocs:myaccount_loginlocation';
  var EVENT_CTA = 'ocs:account_cta';

  // Constant in the production GTM tag; sent from here so the tag maps cleanly.
  var LOGIN_CTA_BUTTON = 'Log In';

  /**
   * Class name -> `section` value, lifted verbatim from the two production GTM
   * Custom HTML tags. Several hooks intentionally share one section: the
   * dashboard links were all reported as `my_account_dashboard`.
   *
   * gtm-back-to-login-myaccount has no Horizon markup today. The mapping is
   * kept so the event stays correct if that control is ever added back.
   */
  var CTA_SECTIONS = {
    'gtm-forgot-password-cta': 'forgot_password',
    'gtm-create-account-cta': 'create_account',
    'gtm-go-back-login-cta': 'go_back_login',
    'gtm-back-to-login-myaccount': 'back_to_login_myaccount',
    'gtm-add-new-address-main': 'gtm_add_new_address_main',
    'gtm-edit-address-main': 'gtm_edit_address_main',
    'gtm-accounts-reset-password': 'my_account_dashboard',
    'gtm-accounts-logout': 'my_account_dashboard',
    'gtm-accounts-order-history': 'my_account_dashboard',
    'gtm-accounts-my-addresses': 'my_account_dashboard',
  };

  var ACCOUNT_TRIGGER_SELECTOR = '.account-button, .header-actions__mobile-account, .icon--account';

  /**
   * Remembered between the delete click and the dialog confirmation. Production
   * fired after window.confirm() returned true, so the event means "address
   * deleted", not "delete clicked" — but by confirm time the originating button
   * is gone from the event path, so the variant is captured on the way in.
   * customer-account.js keeps the same state privately; it is not readable here.
   */
  var pendingDeleteAction = '';

  function publish(name, payload) {
    var api = window.OCSAnalyticsEvents;
    if (!api || typeof api.publishOcsAnalyticsEvent !== 'function') return false;
    return api.publishOcsAnalyticsEvent(name, payload);
  }

  /* ------------------------------------------------------------------ *
   * Pure helpers — no DOM, no window. Everything testable lives here.
   * ------------------------------------------------------------------ */

  /**
   * @param {string[]} classNames
   * @returns {string} the CTA `section` value, or '' when none match.
   */
  function sectionForClassList(classNames) {
    var i;
    for (i = 0; i < classNames.length; i += 1) {
      if (Object.prototype.hasOwnProperty.call(CTA_SECTIONS, classNames[i])) {
        return CTA_SECTIONS[classNames[i]];
      }
    }
    return '';
  }

  /**
   * The `myaccount_address` value for an address form or delete trigger.
   * Reproduces production's two transforms exactly:
   *   form name  -> name.split('__')[1]      ('added' / 'edited')
   *   delete name -> name.replace('gtm-','') ('delete-address-main' / '-form')
   *
   * @param {string[]} classNames
   * @returns {string} '' when the element is not an address hook.
   */
  function addressActionForClassList(classNames) {
    var i;
    var name;
    for (i = 0; i < classNames.length; i += 1) {
      name = classNames[i];
      if (name.indexOf('gtm-') !== 0) continue;
      if (name.indexOf('address-form__') > -1) return name.split('__')[1];
      if (name.indexOf('gtm-delete-address-') === 0) return name.slice(4);
    }
    return '';
  }

  /**
   * Rewrite the register form's return_to so the marketing opt-in survives the
   * redirect to /account. Production did the same, because the flag is only
   * knowable on the register page but is only reported on the account page.
   *
   * @param {string} returnTo
   * @param {boolean} accepts
   * @returns {string}
   */
  function withAcceptsMarketing(returnTo, accepts) {
    var parts = String(returnTo || '').split('?');
    var params = new URLSearchParams(parts[1] || '');
    params.set('accepts_marketing', accepts ? 'true' : 'false');
    return parts[0] + '?' + params.toString();
  }

  /**
   * Production resolved this in a GTM Custom JavaScript variable reading
   * {{Page URL}}; that variable cannot run in the sandbox.
   *
   * @returns {string} 'checkout' on a checkout-initiated login, else ''.
   */
  function checkoutLoginLocation(pathname, search) {
    if (String(pathname || '').indexOf('/account/login') === -1) return '';
    return new URLSearchParams(search || '').has('checkout_url') ? 'checkout' : '';
  }

  /**
   * @param {string[]} keys
   * @returns {string} pathname + remaining query, for history.replaceState.
   */
  function stripSearchParams(pathname, search, keys) {
    var params = new URLSearchParams(search || '');
    var query;
    var i;
    for (i = 0; i < keys.length; i += 1) params.delete(keys[i]);
    query = params.toString();
    return pathname + (query ? '?' + query : '');
  }

  function isRegisterPath(pathname) {
    return String(pathname || '').indexOf('/account/register') > -1;
  }

  /* ------------------------------------------------------------------ *
   * DOM helpers
   * ------------------------------------------------------------------ */

  function classNamesOf(element) {
    var className;
    if (!element) return [];
    if (element.classList && typeof element.classList.length === 'number') {
      return Array.prototype.slice.call(element.classList);
    }
    className = element.className;
    if (typeof className !== 'string') return [];
    return className.split(/\s+/).filter(Boolean);
  }

  function closestCtaHook(element) {
    var node;
    var section;
    for (node = element; node; node = node.parentElement) {
      section = sectionForClassList(classNamesOf(node));
      if (section) return { element: node, section: section };
    }
    return null;
  }

  function labelFor(element) {
    var text = element.innerText || element.textContent || '';
    return String(text).replace(/\s+/g, ' ').trim();
  }

  function replaceUrl(url) {
    if (window.history && typeof window.history.replaceState === 'function') {
      window.history.replaceState({}, '', url);
    }
  }

  /* ------------------------------------------------------------------ *
   * Page-state publishers
   * ------------------------------------------------------------------ */

  /**
   * Login success and account creation both arrive as redirect params rather
   * than as in-page state, because Shopify owns the POST. The params are
   * stripped after publishing so a refresh does not double-count.
   *
   * Nothing here navigates. ?create=true is not proof that an account was
   * created — it lives in the register form's return_to, so Shopify echoes it
   * back on the error re-render too. An earlier version forwarded to /account
   * whenever the register page lacked a verification panel, which bounced a
   * logged-out shopper off their own "that email is already registered" error
   * and onto the sign-in page. The register page now states which of the three
   * outcomes it is showing, and each is handled in place.
   */
  function publishRedirectEvents(scope) {
    var location = window.location;
    var params = new URLSearchParams(location.search || '');
    var acceptsMarketing;

    if (params.has('create')) {
      // Registration failed. No account exists, so there is nothing to report:
      // myaccount_creation has no outcome parameter, unlike myaccount_login and
      // myaccount_password. Production gated its forward the same way, on
      // window.theme.register_errors. Params are still stripped, as they were
      // there, so the error page does not carry them in the address bar.
      if (isRegisterPath(location.pathname) && scope.querySelector('[data-ocs-register-error="true"]')) {
        replaceUrl(stripSearchParams(location.pathname, location.search, ['create', 'accepts_marketing']));
        return;
      }

      acceptsMarketing = params.get('accepts_marketing') === 'true' ? 'true' : 'false';
      publish(EVENT_CREATION, { accepts_marketing: acceptsMarketing });
      replaceUrl(stripSearchParams(location.pathname, location.search, ['create', 'accepts_marketing']));
      return;
    }

    if (params.has('loggedin')) {
      publish(EVENT_LOGIN, { sign_in: 'successful' });
      replaceUrl(stripSearchParams(location.pathname, location.search, ['loggedin']));
    }
  }

  function publishLoginFailure(scope) {
    if (scope.querySelector('[data-ocs-login-state="failed"]')) {
      publish(EVENT_LOGIN, { sign_in: 'failed' });
    }
  }

  function publishRecoverOutcome(scope) {
    var panel = scope.querySelector('[data-ocs-recover-state]');
    var state = panel && panel.getAttribute('data-ocs-recover-state');
    if (state === 'success' || state === 'failure') {
      publish(EVENT_PASSWORD, { reset_email: state });
    }
  }

  function publishCheckoutLoginLocation() {
    var location = checkoutLoginLocation(window.location.pathname, window.location.search);
    if (!location) return;
    publish(EVENT_LOGIN_LOCATION, { login_location: location, cta_button: LOGIN_CTA_BUTTON });
  }

  /* ------------------------------------------------------------------ *
   * Bindings
   * ------------------------------------------------------------------ */

  function bindAcceptsMarketing(scope) {
    var checkbox = scope.querySelector('.js-accepts-marketing');
    var form = checkbox && typeof checkbox.closest === 'function' ? checkbox.closest('form') : null;
    var returnToInput = form && form.querySelector('input[name="return_to"]');

    if (!returnToInput) return;

    function sync() {
      returnToInput.value = withAcceptsMarketing(returnToInput.value, checkbox.checked);
    }

    checkbox.addEventListener('change', sync);
    sync();
  }

  function bindAddressFormSubmits(scope) {
    scope.addEventListener(
      'submit',
      function handleAddressSubmit(event) {
        var form = event.target;
        var action;
        if (!form) return;
        action = addressActionForClassList(classNamesOf(form));
        if (action) publish(EVENT_ADDRESSES, { myaccount_address: action });
      },
      true
    );
  }

  function bindClicks(scope) {
    scope.addEventListener(
      'click',
      function handleAccountClick(event) {
        var target = event.target;
        var deleteTrigger;
        var accountTrigger;
        var cta;

        if (!target || typeof target.closest !== 'function') return;

        // Capture phase, and registered before customer-account.js upgrades its
        // custom element, so this runs ahead of the Shopify.postLink navigation.
        if (target.closest('[data-customer-address-delete-confirm]')) {
          if (pendingDeleteAction) publish(EVENT_ADDRESSES, { myaccount_address: pendingDeleteAction });
          pendingDeleteAction = '';
          return;
        }

        deleteTrigger = target.closest('.js_delete_address');
        if (deleteTrigger) {
          pendingDeleteAction = addressActionForClassList(classNamesOf(deleteTrigger));
          return;
        }

        accountTrigger = target.closest(ACCOUNT_TRIGGER_SELECTOR);
        if (accountTrigger) {
          // Signed-in shoppers count too. Production gates on the CSS match
          // alone: rule 17 fires for `.my-account-widget,
          // .js-menu__account-link, .icon.icon--account` and macro 67 returns
          // `global_header` for any of them, with no test of customer state.
          // The header-actions ancestor stays required — that is what makes
          // `global_header` the true answer rather than a guess.
          if (accountTrigger.closest('header-actions')) {
            publish(EVENT_LOGIN_LOCATION, {
              login_location: 'global_header',
              cta_button: LOGIN_CTA_BUTTON,
            });
          }
          return;
        }

        cta = closestCtaHook(target);
        if (cta) {
          publish(EVENT_CTA, { section: cta.section, cta_title_pos: labelFor(cta.element) });
        }
      },
      true
    );
  }

  /* ------------------------------------------------------------------ */

  function init(scope) {
    var root = scope || document;

    publishRedirectEvents(root);
    publishLoginFailure(root);
    publishRecoverOutcome(root);
    publishCheckoutLoginLocation();

    bindAcceptsMarketing(root);
    bindAddressFormSubmits(root);
    bindClicks(root);
  }

  window.OCSAccountAnalytics = {
    CTA_SECTIONS: CTA_SECTIONS,
    addressActionForClassList: addressActionForClassList,
    checkoutLoginLocation: checkoutLoginLocation,
    init: init,
    isRegisterPath: isRegisterPath,
    sectionForClassList: sectionForClassList,
    stripSearchParams: stripSearchParams,
    withAcceptsMarketing: withAcceptsMarketing,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function bootstrap() {
      init(document);
    });
  } else {
    init(document);
  }
})();
